import { and, eq, or } from "drizzle-orm";
import {
  category,
  type PowerSyncDrizzleDB,
  tx,
  user,
  user_exchange_rate,
  wallet,
  wallet_group,
} from "@cashsync/powersync";
import { nowISO } from "../utils/date-helpers";
import { generateId } from "@fleet/utils";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";

/**
 * Local mutations for financial domain. Written to PowerSync SQLite;
 * connector uploads them to server (write-back) in the background.
 * Amounts ALWAYS in minor units (integers).
 *
 * Every `UPDATE … WHERE id = ?` in this file goes through `patchRowVerified`: a
 * raw UPDATE against the PowerSync view can match ZERO rows and return
 * "success" while the newly created row is still traveling to the server (see
 * `../powersync/verified-write.ts`). These mutations **can throw**
 * (`LocalWriteLostError`): caller must report it, never swallow it.
 * `updateUserFinanceSettingsMutation` (user financial settings) is NOT an
 * exception: it is the SAME `user` row that local `ThemeProvider` seeds for
 * any `uid` without waiting for `hasSynced` — the window exists just like in
 * `wallet`/`tx`. Audit: `infra/reports/user-exclusion-audit/2026-08-13-summary.md`.
 */

// ─── Wallets ──────────────────────────────────────────────────────────────────

export interface WalletDraft {
  name: string;
  kind: string;
  currency: string;
  initialBalance?: number;
  icon?: string | null;
  color?: string | null;
  groupId?: string | null;
  excludeFromTotals?: boolean;
  sortOrder?: number;
}

export async function createWalletMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  draft: WalletDraft,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(wallet).values({
    id,
    user_id: userId,
    name: draft.name.trim(),
    kind: draft.kind,
    currency: draft.currency,
    initial_balance: draft.initialBalance ?? 0,
    icon: draft.icon ?? null,
    color: draft.color ?? null,
    group_id: draft.groupId ?? null,
    sort_order: draft.sortOrder ?? 0,
    archived: 0,
    exclude_from_totals: draft.excludeFromTotals ? 1 : 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateWalletMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{
    name: string;
    kind: string;
    currency: string;
    initial_balance: number;
    icon: string | null;
    color: string | null;
    group_id: string | null;
    sort_order: number;
    archived: number;
    exclude_from_totals: number;
  }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const finalUpdates = { ...updates };
  if (finalUpdates.name !== undefined) {
    finalUpdates.name = finalUpdates.name.trim();
  }
  await patchRowVerified(db, wallet, wallet.id, id, finalUpdates, options);
}

/** Archiving preserves history; the account stops appearing in listings. */
export async function archiveWalletMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  archived: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await updateWalletMutation(db, id, { archived: archived ? 1 : 0 }, options);
}

/** Logical deletion: balance and transactions remain in history. */
export async function softDeleteWalletMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, wallet, wallet.id, id, { deleted_at: nowISO() }, options);
}

export async function createWalletGroupMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  name: string,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(wallet_group).values({
    id,
    user_id: userId,
    name: name.trim(),
    sort_order: 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

// ─── Categories ───────────────────────────────────────────────────────────────

export interface CategoryDraft {
  kind: "expense" | "income";
  name: string;
  parentId?: string | null;
  icon?: string | null;
  color?: string | null;
  sortOrder?: number;
}

export async function createCategoryMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  draft: CategoryDraft,
): Promise<string> {
  const id = generateId();
  const now = nowISO();
  await db.insert(category).values({
    id,
    user_id: userId,
    parent_id: draft.parentId ?? null,
    kind: draft.kind,
    name: draft.name.trim(),
    icon: draft.icon ?? null,
    color: draft.color ?? null,
    sort_order: draft.sortOrder ?? 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateCategoryMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{
    name: string;
    parent_id: string | null;
    icon: string | null;
    color: string | null;
    sort_order: number;
  }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, category, category.id, id, updates, options);
}

/** Logical deletion: transactions retain reference for history. */
export async function softDeleteCategoryMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, category, category.id, id, { deleted_at: nowISO() }, options);
}

// ─── Transacciones ────────────────────────────────────────────────────────────

export interface TransactionDraft {
  kind: "expense" | "income" | "transfer";
  /** Unidades menores de `currency`. Siempre positivo. */
  amount: number;
  currency: string;
  /** Amount materialized in base currency (D2). If omitted, = amount. */
  baseAmount?: number;
  baseCurrency?: string;
  exchangeRateUsed?: number | null;
  walletId: string;
  toWalletId?: string | null;
  /**
   * Amount credited to destination account (minor units of ITS currency)
   * for transfers between different currencies. `null`/omitted = same amount.
   */
  toAmount?: number | null;
  feeAmount?: number | null;
  categoryId?: string | null;
  occurredAtISO: string;
  note?: string | null;
  payee?: string | null;
  projectId?: string | null;
  recurringRuleId?: string | null;
  isAdjustment?: boolean;
  /** Permite IDs deterministas (recurrentes UUIDv5). */
  id?: string;
}

export async function createTransactionMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  draft: TransactionDraft,
): Promise<string> {
  if (draft.kind === "transfer" && (!draft.toWalletId || draft.toWalletId === draft.walletId)) {
    throw new Error("Una transferencia requiere cuenta destino distinta");
  }
  if (draft.amount < 0) {
    throw new Error("The amount cannot be negative");
  }
  const id = draft.id ?? generateId();
  const now = nowISO();
  await db.insert(tx).values({
    id,
    user_id: userId,
    kind: draft.kind,
    amount: draft.amount,
    currency: draft.currency,
    base_amount: draft.baseAmount ?? draft.amount,
    base_currency: draft.baseCurrency ?? draft.currency,
    // PG numeric ≡ text in the client SQLite: the rate travels as a string.
    exchange_rate_used: draft.exchangeRateUsed == null ? null : String(draft.exchangeRateUsed),
    wallet_id: draft.walletId,
    to_wallet_id: draft.toWalletId ?? null,
    to_amount: draft.toAmount ?? null,
    fee_amount: draft.feeAmount ?? null,
    category_id: draft.categoryId ?? null,
    occurred_at: draft.occurredAtISO,
    note: draft.note ?? null,
    payee: draft.payee ?? null,
    project_id: draft.projectId ?? null,
    recurring_rule_id: draft.recurringRuleId ?? null,
    is_adjustment: draft.isAdjustment ? 1 : 0,
    cleared: 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateTransactionMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  updates: Partial<{
    kind: string;
    amount: number;
    currency: string;
    base_amount: number;
    base_currency: string;
    /** PG numeric ≡ text en cliente. */
    exchange_rate_used: string | null;
    wallet_id: string;
    to_wallet_id: string | null;
    /** Importe acreditado en destino (transferencias cross-divisa). */
    to_amount: number | null;
    fee_amount: number | null;
    category_id: string | null;
    occurred_at: string;
    note: string | null;
    payee: string | null;
    project_id: string | null;
    /** Reconciliation: 1 = verified against statement, 0 = pending. */
    cleared: number;
  }>,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, tx, tx.id, id, updates, options);
}

/**
 * Reconciliation: marks/unmarks a transaction as verified ("cleared")
 * against a statement. Does not alter actual derived balance of account; only
 * feeds the reconciled total against which statement is matched.
 */
export async function setTransactionClearedMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  cleared: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, tx, tx.id, id, { cleared: cleared ? 1 : 0 }, options);
}

/** Logical deletion (recoverable; balance history excludes it instantly). */
export async function softDeleteTransactionMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, tx, tx.id, id, { deleted_at: nowISO() }, options);
}

/** Duplicate: same transaction with today's date and new id. */
export async function duplicateTransactionMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  sourceId: string,
): Promise<string | null> {
  const rows = await db.select().from(tx).where(eq(tx.id, sourceId)).limit(1);
  const source = rows[0];
  if (!source) {
    return null;
  }
  const id = generateId();
  const now = nowISO();
  await db.insert(tx).values({
    ...source,
    id,
    user_id: userId,
    occurred_at: now,
    created_at: now,
    updated_at: now,
    deleted_at: null,
    recurring_rule_id: null,
  });
  return id;
}

// ─── User financial settings ─────────────────────────────────────────────────

/** Valid range for accounting month start day (clamp, see period.ts). */
const MONTH_START_DAY_MIN = 1;
const MONTH_START_DAY_MAX = 28;

export interface FinanceSettingsUpdates {
  /** Base currency (ISO 4217). Changing it DOES NOT recalculate history (D2). */
  baseCurrency?: string;
  /** Accounting month start day; clamped to 1..28. */
  monthStartDay?: number;
}

/**
 * Persists financial settings in local `user` row (write-back
 * of `user` already accepts base_currency/month_start_day).
 *
 * Uses `patchRowVerified`, same as rest of file: `user` is not
 * excluded (see header). `updated_at` stamped by helper is only
 * local/optimistic — server write-back sets it definitively and ignores
 * client value, same as before.
 */
export async function updateUserFinanceSettingsMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  updates: FinanceSettingsUpdates,
  options?: VerifiedWriteOptions,
): Promise<void> {
  const set: { base_currency?: string; month_start_day?: number } = {};
  if (updates.baseCurrency !== undefined) {
    set.base_currency = updates.baseCurrency.toUpperCase();
  }
  if (updates.monthStartDay !== undefined) {
    set.month_start_day = Math.min(
      MONTH_START_DAY_MAX,
      Math.max(MONTH_START_DAY_MIN, Math.trunc(updates.monthStartDay)),
    );
  }
  if (Object.keys(set).length === 0) {
    return;
  }
  await patchRowVerified(db, user, user.id, userId, set, options);
}

// ─── User exchange rates by date ─────────────────────────────────────────────

export interface UserExchangeRateDraft {
  /** Source currency (ISO 4217). */
  currency: string;
  /** Target base currency (ISO 4217). */
  baseCurrency: string;
  /** Rate `1 currency = rate baseCurrency` (decimal > 0). */
  rate: number;
  /** Date (YYYY-MM-DD) from which the rate takes effect. */
  asOf: string;
}

/**
 * Sets (or replaces) `currency → baseCurrency` rate effective from `asOf`.
 * Upsert by logical key (user, currency, base, date): registering twice
 * for the same date updates the rate instead of duplicating the row.
 */
export async function upsertUserExchangeRateMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  draft: UserExchangeRateDraft,
): Promise<string> {
  const currency = draft.currency.toUpperCase();
  const baseCurrency = draft.baseCurrency.toUpperCase();
  const asOf = draft.asOf.slice(0, 10);
  if (!(draft.rate > 0) || !Number.isFinite(draft.rate)) {
    throw new Error("The rate must be a positive number");
  }
  const now = nowISO();
  const existing = await db
    .select({ id: user_exchange_rate.id })
    .from(user_exchange_rate)
    .where(
      and(
        eq(user_exchange_rate.user_id, userId),
        eq(user_exchange_rate.currency, currency),
        eq(user_exchange_rate.base_currency, baseCurrency),
        eq(user_exchange_rate.as_of, asOf),
      ),
    )
    .limit(1);
  const found = existing[0];
  if (found) {
    await patchRowVerified(db, user_exchange_rate, user_exchange_rate.id, found.id, {
      rate: String(draft.rate),
      deleted_at: null,
    });
    return found.id;
  }
  const id = generateId();
  await db.insert(user_exchange_rate).values({
    id,
    user_id: userId,
    currency,
    base_currency: baseCurrency,
    rate: String(draft.rate),
    as_of: asOf,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/** Logical deletion of a user exchange rate. */
export async function deleteUserExchangeRateMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    user_exchange_rate,
    user_exchange_rate.id,
    id,
    { deleted_at: nowISO() },
    options,
  );
}

// ─── Trash (ACTIVITY-CORE L2): restore / purge ────────────────────────────────
//
// Soft-delete (deleted_at) is already done by `softDelete*Mutation`; live
// views filter `deleted_at IS NULL`. Here we handle restore (deleted_at → null) and
// purge (hard DELETE via write-path, leaving snapshot in history).
// Server logs restore as `restored` and purge as `deleted`.

/** Restores a wallet from trash (its derived balance recalculates automatically). */
export async function restoreWalletMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, wallet, wallet.id, id, { deleted_at: null }, options);
}

/** Restores a transaction from trash. */
export async function restoreTransactionMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, tx, tx.id, id, { deleted_at: null }, options);
}

/** Restores a category from trash. */
export async function restoreCategoryMutation(
  db: PowerSyncDrizzleDB,
  id: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, category, category.id, id, { deleted_at: null }, options);
}

/** Permanent purge of a transaction (hard DELETE; history stores snapshot). */
export async function purgeTransactionMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db.delete(tx).where(eq(tx.id, id));
}

/**
 * Permanent purge of a wallet. Manually purges its transactions in cascade
 * (txs whose source or destination is the wallet) and then the wallet.
 */
export async function purgeWalletMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db.delete(tx).where(or(eq(tx.wallet_id, id), eq(tx.to_wallet_id, id)));
  await db.delete(wallet).where(eq(wallet.id, id));
}

/** Permanent purge of a category (txs retain dangling category_id). */
export async function purgeCategoryMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  await db.delete(category).where(eq(category.id, id));
}
